import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { Component, h, Method, Prop, State } from "@stencil/core"
import { getSupportedLocaleData, getLanguageHandler } from "../../languageHandler/LanguageHandler"

/**
 * The component with a checkbox and info about sharing the details to the Click to Pay System.
 */
@Component({
  tag: "consent-checkbox",
  styleUrl: "ConsentCheckbox.scss",
})
export class ConsentCheckbox {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /**
   * Indicator when the checkbox was not checked.
   *
   * @default false
   */
  @Prop() error: boolean

  /** Fully translated label. Defaults to the existing consent text. */
  @Prop() label?: string

  /** Whether to use the compact label style. */
  @Prop() compact: boolean = false

  /** Whether the checkbox is disabled. */
  @Prop() disabled: boolean = false

  /** Controlled checked state. When omitted, the checkbox manages its own state. */
  @Prop() checked?: boolean

  /**
   * Callback called after consent checkbox change.
   *
   * @param isChecked Value with the state value of the checkbox
   */
  @Prop() consentCheckboxOnChange?: (isChecked: boolean) => void

  @State() componentUniqueId: string

  @State() isChecked: boolean = false

  private languageHandler: LanguageHandler
  private inputElement?: HTMLInputElement

  componentWillLoad() {
    this.componentUniqueId = Math.random().toString()
    const supportedLang = getSupportedLocaleData(this.locale).lang
    this.languageHandler = getLanguageHandler(supportedLang)
  }

  onChange = (event: Event) => {
    if (this.disabled) {
      return
    }

    const isChecked = (event.target as HTMLInputElement).checked
    this.isChecked = isChecked
    if (typeof this.consentCheckboxOnChange === "function") {
      this.consentCheckboxOnChange(isChecked)
    }
  }

  /** Focuses the native consent checkbox. */
  @Method()
  async focusInput() {
    this.inputElement?.focus()
  }

  render() {
    const isChecked = this.checked ?? this.isChecked

    return (
      <div
        class={
          "checkboxWrapper " +
          (isChecked ? "checkboxWrapperChecked " : "") +
          (this.disabled ? "checkboxWrapperDisabled" : "")
        }>
        <div class="checkboxRow">
          <div class="checkboxInput">
            <input
              ref={element => (this.inputElement = element)}
              id={`${this.componentUniqueId}_consentCheckbox`}
              type="checkbox"
              class={!this.error ? "checkbox" : "checkboxError"}
              checked={isChecked}
              disabled={this.disabled}
              onChange={this.onChange}
            />
          </div>

          <div class={this.compact ? "labelGridCell labelGridCellCompact" : "labelGridCell"}>
            <label class="checkboxLabel" htmlFor={`${this.componentUniqueId}_consentCheckbox`}>
              {this.label ?? this.languageHandler.getString("consentText")}
            </label>
          </div>
        </div>
      </div>
    )
  }
}
